import React, {
  ReactElement,
  useMemo,
  useCallback,
  useState,
  createContext,
  useContext,
} from 'react';
import { FaChevronRight } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from '../../../colors';
import {
  formatBalance,
  formatPercentage,
} from '../../../helpers/NumberFormatter';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import {
  selectSideMenuCollapsed,
  toggleSideMenuCollapsed,
} from '../../../store/slices/ui';
import { HiddenValue } from '../../../screens/Overview/Content/ValueHideContext';
import './index.css';

const noop = (): void => undefined;

const SideMenuCollapseContext = createContext<{
  collapsed: boolean;
  toggle: () => void;
}>({
  collapsed: false,
  toggle: noop,
});
export const useSideMenuCollapse = () => useContext(SideMenuCollapseContext);

export const SideMenu = ({
  hasPositiveProfit,
  children,
}: {
  hasPositiveProfit?: boolean;
  children: ReactElement | Array<ReactElement | undefined> | undefined;
}) => {
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSideMenuCollapsed);
  const toggle = useCallback(() => {
    dispatch(toggleSideMenuCollapsed());
  }, [dispatch]);

  const sideMenuBackgroundColor = useMemo(() => {
    if (typeof hasPositiveProfit === 'undefined') {
      return colors.black;
    }

    return hasPositiveProfit ? 'rgb(11, 19, 12)' : 'rgb(28, 15, 15)';
  }, [hasPositiveProfit]);

  return (
    <SideMenuCollapseContext.Provider value={{ collapsed, toggle }}>
      <div
        className={`side-menu-container${
          collapsed ? ' side-menu-collapsed' : ''
        }`}
        style={{ backgroundColor: sideMenuBackgroundColor }}
      >
        <button
          className="side-menu-collapse-btn"
          onClick={toggle}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            {collapsed ? (
              <path
                d="M4 4h10M4 9h10M4 14h10"
                stroke="#808080"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            ) : (
              <path
                d="M12 4L6 9l6 5"
                stroke="#808080"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            )}
          </svg>
        </button>
        <div className="side-menu-content">{children}</div>
      </div>
    </SideMenuCollapseContext.Provider>
  );
};

export const SideMenuSection = ({ children }: { children: any }) => {
  return <div className="side-menu-section">{children}</div>;
};

export const SideMenuTitle = ({ title }: { title: string }) => {
  return (
    <div className="side-menu-section-title-container">
      <h3 className="side-menu-section-title">{title}</h3>
    </div>
  );
};

export const SideMenuSubTitle = ({ title }: { title: string }) => {
  return (
    <div className="side-menu-section-subtitle-container">
      <h3 className="side-menu-section-subtitle">{title}</h3>
    </div>
  );
};

export const SideMenuItem = ({
  id,
  label,
  value,
  delta,
  onClick,
  isActive,
}: {
  id: number | string;
  label: string;
  value?: number;
  delta?: number;
  onClick?: (id: any) => void;
  isActive?: boolean;
}) => {
  const getTextColor = isActive ? colors.white : '#808080';
  const deltaColor =
    delta === 0 || typeof delta === 'undefined'
      ? colors.white
      : delta > 0
      ? colors.green
      : colors.red;

  return (
    <div
      onClick={() => onClick?.(id)}
      className="side-menu-section-item"
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div>
        <h4
          className={`side-menu-section-item-text-small ${
            onClick ? 'side-menu-pressable' : ''
          }`}
          style={{ color: getTextColor }}
        >
          {label}
        </h4>
        <div className="side-menu-section-item-row">
          {!!value && (
            <h5 className="side-menu-section-item-text-value">
              <HiddenValue>{formatBalance(value, true)}</HiddenValue>
            </h5>
          )}
          {typeof delta !== 'undefined' && (
            <h5
              className={'side-menu-section-item-text-delta'}
              style={{ color: deltaColor }}
            >
              {formatPercentage(delta, 2)}
            </h5>
          )}
        </div>
      </div>
      {isActive && (
        <FaChevronRight color={colors.white} size={18} className={'chevron'} />
      )}
    </div>
  );
};

export const SideMenuSelect = ({
  activeItem,
  options,
}: {
  activeItem: ReactElement;
  options: ReactElement[];
}) => {
  // Always display the activeItem, render options when clicked
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginRight: 16,
          cursor: 'pointer',
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {activeItem}
        {isOpen ? (
          <FaChevronUp color={colors.white} size={24} />
        ) : (
          <FaChevronDown color={colors.gray} size={24} />
        )}
      </div>

      <div
        style={{
          maxHeight: isOpen ? 500 : 0,
          transition: 'max-height 0.5s ease-out',
        }}
        className="side-menu-section-options"
      >
        {options?.map((o, i) => (
          <div key={`options_item_${i}`} onClick={() => setIsOpen(false)}>
            {o}
          </div>
        ))}
      </div>
    </div>
  );
};
